import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface Asset {
  id: string
  name: string
  type: string
  category: string
  value: number
  growth: number
  risk: string
  status: 'active' | 'pending' | 'closed'
  manager: string
  createdDate: string
  tags: string[]
  description: string
}

export interface AssetsState {
  loading: boolean
  error: string | null
  assets: Asset[]
  filters: {
    search: string
    type: string
    risk: string
    status: string
    minValue: number | null
    maxValue: number | null
  }
  pagination: {
    current: number
    pageSize: number
    total: number
  }
  favorites: string[]
}

const initialState: AssetsState = {
  loading: false,
  error: null,
  assets: [],
  filters: {
    search: '',
    type: 'all',
    risk: 'all',
    status: 'all',
    minValue: null,
    maxValue: null,
  },
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  favorites: ['1', '3'],
}

// 异步thunk
export const fetchAssets = createAsyncThunk(
  'assets/fetchAssets',
  async () => {
    // 模拟API调用
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 模拟数据
    const mockAssets: Asset[] = [
      {
        id: 'AST001',
        name: '科技成长股票基金',
        type: '股票基金',
        category: '权益类',
        value: 2500000,
        growth: 15.2,
        risk: '中高',
        status: 'active',
        manager: '张经理',
        createdDate: '2023-06-15',
        tags: ['高增长', '科技', '长期'],
        description: '专注于科技行业的成长型股票基金',
      },
      {
        id: 'AST002',
        name: '稳健债券投资组合',
        type: '债券基金',
        category: '固收类',
        value: 1800000,
        growth: 4.8,
        risk: '低',
        status: 'active',
        manager: '李经理',
        createdDate: '2023-08-22',
        tags: ['稳健', '低风险', '定期收益'],
        description: '以国债和企业债为主的稳健型投资组合',
      },
      {
        id: 'AST003',
        name: '商业地产信托',
        type: '房地产',
        category: '不动产',
        value: 3500000,
        growth: 8.5,
        risk: '中',
        status: 'active',
        manager: '王经理',
        createdDate: '2023-03-10',
        tags: ['不动产', '租金收益', '保值'],
        description: '商业地产投资信托，稳定租金收益',
      },
      {
        id: 'AST004',
        name: '数字货币投资组合',
        type: '数字货币',
        category: '另类投资',
        value: 800000,
        growth: 32.7,
        risk: '高',
        status: 'active',
        manager: '赵经理',
        createdDate: '2023-11-05',
        tags: ['高波动', '新兴', '区块链'],
        description: '主流数字货币的投资组合',
      },
      {
        id: 'AST005',
        name: '新能源产业基金',
        type: '产业基金',
        category: '权益类',
        value: 1200000,
        growth: 22.3,
        risk: '中高',
        status: 'pending',
        manager: '孙经理',
        createdDate: '2024-01-08',
        tags: ['新能源', '环保', '政策支持'],
        description: '投资于新能源产业链的专项基金',
      },
      {
        id: 'AST006',
        name: '黄金ETF基金',
        type: '贵金属',
        category: '大宗商品',
        value: 950000,
        growth: 6.2,
        risk: '中低',
        status: 'active',
        manager: '周经理',
        createdDate: '2023-09-18',
        tags: ['避险', '保值', '通胀对冲'],
        description: '跟踪黄金价格的交易所交易基金',
      },
    ]
    
    return mockAssets
  }
)

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AssetsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.current = 1 // 重置页码
    },
    setPagination: (state, action: PayloadAction<Partial<AssetsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
      state.pagination.current = 1
    },
    addAsset: (state, action: PayloadAction<Asset>) => {
      state.assets.unshift(action.payload)
      state.pagination.total = state.assets.length
    },
    updateAsset: (state, action: PayloadAction<{ id: string; updates: Partial<Asset> }>) => {
      const index = state.assets.findIndex(asset => asset.id === action.payload.id)
      if (index !== -1) {
        state.assets[index] = { ...state.assets[index], ...action.payload.updates }
      }
    },
    deleteAsset: (state, action: PayloadAction<string>) => {
      state.assets = state.assets.filter(asset => asset.id !== action.payload)
      state.pagination.total = state.assets.length
    },
    toggleFavorite: (state, action: PayloadAction<string>) => {
      const index = state.favorites.indexOf(action.payload)
      if (index === -1) {
        state.favorites.push(action.payload)
      } else {
        state.favorites.splice(index, 1)
      }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssets.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.loading = false
        state.assets = action.payload
        state.pagination.total = action.payload.length
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '获取资产数据失败'
      })
  },
})

export const {
  setFilters,
  setPagination,
  clearFilters,
  addAsset,
  updateAsset,
  deleteAsset,
  toggleFavorite,
  clearError,
} = assetsSlice.actions

export default assetsSlice.reducer